import axios, { AxiosResponse } from 'axios';
import {
  ExamSession,
  ExamSessionDto,
  ImportedExamSession,
  ExamFromExternalSystem,
  CopyExamSheetDto,
  ExamSessionSimpleDto,
  CopyExamSheetResponse,
  CopyExamSheetOperation,
} from '../../../models/examSessionDto.types';
import { SessionListsResponse } from '../../../models/sessionListItem.types';
import {
  IExamSession,
  ISavedSessionImportedFromExternalSystem,
  ISavedSessionCreatedManually,
  IDeleteResult,
  IExternalSystem,
  IIdResult,
  ExamSheet,
  CanExamSheetBeConfiguredResults,
  examScoreValueCommentRequired,
  CriteriaSetupDto,
  CriteriaSetupResponseDto,
  ISheetTypesForExamFromExternalSystem,
  dtoSioePkz,
  IDeleteClosed,
} from '../../../models/session.types';
import EnvWindow from '../../../utils/env';
import { CardLimitsData } from '../../../pages/Sessions/SessionsParametersDialog/SessionsParametersCardLimit/SessionsParametersCardLimit.types';
import {
  CardLimits,
  UpdateCardLimitsResponse,
} from '../../../pages/Sessions/SessionsParametersDialog/SessionsParametersDialogContent/SessionsParametersDialogContent.types';
import { ITaskGroup } from '../../../models/taskGroup.types';
import { addParam, splitParams } from '../../../utils/servicesHelper';
import { ExamSheetFile } from '../../../pages/Sessions/SessionsImages/SessionsImagesManagment/SessionImagesManagment.types';
import { CanSessionBeSetToClosed, CanSessionBeSetToOpen } from '../../../components/EditSession/DetailsProps.types';
import { BackendErrorCode, FileType } from '../../../components/ExamResultsRepository/ExamResultsRepository.types';

export async function getSessionsApi(
  signal?: AbortSignal,
  pageSize = 20,
  page = 0,
  sortByProperty?: string,
  isDescending?: boolean,
  sessionName?: string,
  examType?: string,
  subject?: string,
  isConfigured?: string,
  sheetCode?: string,
  year?: string,
  month?: string,
  sourceSystemType?: string,
  archiveFrom?: string,
  archiveTo?: string,
  state?: string,
): Promise<SessionListsResponse> {
  let params = '';
  params += addParam('sessionName', sessionName ? sessionName : '');
  params += addParam('examType', examType ? examType : '');
  params += addParam('subject', subject ? subject : '');
  params += addParam('sheetCode', sheetCode ? sheetCode : '');
  params += addParam('year', year ? year : '');
  params += splitParams('month', month ? month : '');
  params += splitParams('sourceSystemType', sourceSystemType ? sourceSystemType : '');
  params += addParam('sortByProperty', sortByProperty ? sortByProperty : '');
  params += addParam('isDescending', isDescending ? isDescending : '');
  params += addParam(
    'isConfigured',
    isConfigured !== '' && isConfigured !== '1,2' && isConfigured !== '2,1'
      ? isConfigured === '1'
        ? 'true'
        : 'false'
      : '',
  );
  params += addParam(
    'hasNonClosedAssessmentSessions',
    state !== '' && state !== '1,2' && state !== '2,1' ? (state === '1' ? 'true' : 'false') : '',
  );
  params += addParam('archiveAfterStartDate', archiveFrom ? archiveFrom : '');
  params += addParam('archiveAfterEndDate', archiveTo ? archiveTo : '');

  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/Query?page=${page}&pageSize=${pageSize}${params}`,
    { signal: signal ?? undefined },
  );

  return response.data;
}

export async function sessionsFromExternalSystem(systemName: string): Promise<ImportedExamSession[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN_API_URL}/${systemName}/examSession`);

  return response.data;
}

export async function examsForSessionFromExternalSystem(
  systemName: string,
  idExamSession: number,
): Promise<ExamFromExternalSystem[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN_API_URL}/${systemName}/examSession/${idExamSession}`);

  return response.data;
}

export async function examsForSessionFromExternalSystemSioePkz(
  systemName: string,
  idExamSession: number,
): Promise<ExamFromExternalSystem[]> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_ADMIN_API_URL}/${systemName}/examSession/${idExamSession}/Qualification`,
  );

  return response.data;
}

export async function getSessionDetails(assessmentSessionId: string | null): Promise<IExamSession> {
  const response = await axios.get(
    assessmentSessionId !== null
      ? `${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/${assessmentSessionId}/details`
      : `${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/details`,
  );

  return response.data;
}

export async function getCriteriaSetup(examTaskId: string): Promise<CriteriaSetupResponseDto[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask/${examTaskId}/CriteriaSetup`);

  return response.data;
}

export async function saveCriteriaSetup(
  examGradeRuleId: string,
  requestBody: CriteriaSetupDto[],
): Promise<CriteriaSetupDto[]> {
  const response = await axios.post(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/CriteriaSetup/ExamGradeRuleId/${examGradeRuleId}`,
    {
      criteriaSetups: requestBody,
    },
  );

  return response.data;
}

export async function getSheetTypesForExamFromExternalSystem(
  systemName: string,
  examId: number,
  examCode: string,
  examSessionNumber: number,
): Promise<ISheetTypesForExamFromExternalSystem[]> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_ADMIN_API_URL}/${systemName}/exam/${examId}/examCode/${examCode}/examSessionNumber/${examSessionNumber}/sheetType`,
  );

  return response.data;
}

export async function getSheetTypesForExamFromExternalSystemSioePkz(
  sessionId: number,
  qualificationId: number,
): Promise<ISheetTypesForExamFromExternalSystem[]> {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_ADMIN_API_URL}/Sioepkz/ExamSession/${sessionId}/Qualification/${qualificationId}/ExamSheet`,
  );

  return response.data;
}

export async function saveSessionImportedFromExternalSystem(
  examSession: ExamSessionDto | dtoSioePkz,
  systemName: string | undefined,
): Promise<AxiosResponse<ISavedSessionImportedFromExternalSystem>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/${systemName}`, examSession);

  return response;
}

export async function saveSessionCreatedManually(
  examSession: ExamSession,
): Promise<AxiosResponse<ISavedSessionCreatedManually>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/manual`, examSession);

  return response;
}

export async function deleteSession(id: string): Promise<AxiosResponse<IDeleteResult>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}/${id}`);

  return response;
}

export async function deleteClosedSession(examSheetId: string): Promise<AxiosResponse<IDeleteClosed>> {
  const response = await axios.delete(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/archive`);

  return response;
}

export async function getExternalSystemsList(): Promise<IExternalSystem[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_ADMIN__ADMIN_API_URL}/ExternalSystems`);

  return response.data;
}

export async function getExamGradeRuleFromExamSheet(
  id: string,
  signal: AbortSignal,
): Promise<AxiosResponse<ArrayBuffer>> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${id}/document`, {
    signal: signal,
    responseType: 'arraybuffer',
  });

  return response;
}

//PUT METHODS FOR EXAMS MICROSERVICE
export async function modifyExamSession(
  examSessionId: string,
  name: string,
  sessionType: string,
  year: string,
  month: string,
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_EXAMS__EXAM_SESSION_API_URL}`, {
    examSessionId: examSessionId,
    name: name,
    sessionType: sessionType,
    year: Number(year),
    month: Number(month),
  });

  return response;
}

export async function modifyExam(examId: string, name: string, code: string): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/Exams`, {
    examId: examId,
    name: name,
    code: code,
  });

  return response;
}

export async function modifyExamSheet(
  id: string,
  code: string,
  name: string,
  gradeRulesImageFilePath: File | undefined,
  retentionPeriod: number | null,
): Promise<AxiosResponse<IIdResult>> {
  const requestBody = new FormData();
  requestBody.append('OneId', id);
  requestBody.append('Code', code);
  requestBody.append('Name', name);

  if (gradeRulesImageFilePath) {
    requestBody.append('FormFile', gradeRulesImageFilePath);
  }

  requestBody.append('RetentionPeriod', retentionPeriod?.toString() ?? '');

  const response = await axios({
    method: 'PUT',
    url: `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });

  return response;
}

export async function modifyExamScoringSchema(
  examScoringSchemaId: string,
  label: string,
  criterionName: string,
  criterionNumber: string,
  examScoreValues: number[],
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL, {
    examScoringSchemaId,
    label: label,
    criterionName: criterionName,
    criterionNumber: criterionNumber,
    examScoreValues: examScoreValues,
  });

  return response;
}

export async function modifyExamScoringSchemaForcedComments(
  examScoringSchemaId: string,
  isCommentRequiredEnabled: boolean,
  examScoreValueCommentRequiredList: examScoreValueCommentRequired[],
): Promise<AxiosResponse<boolean>> {
  const response = await axios.put(
    `${EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL}/${examScoringSchemaId}/CommentRequired`,
    {
      examScoringSchemaId,
      isCommentRequiredEnabled,
      examScoreValueCommentRequiredList,
    },
  );

  return response;
}

export async function modifyExamTask(id: string, examTaskNumber: string): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask`, {
    id: id,
    examTaskNumber: examTaskNumber,
  });

  return response;
}

export async function modifyExamTaskGroup(
  id: string,
  name: string,
  dateFrom: string,
  dateTo: string,
  stateId: number,
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`, {
    id: id,
    name: name,
    dateFrom: dateFrom,
    dateTo: dateTo,
    stateId: stateId,
  });

  return response;
}

export async function DeleteExamSheetsAttachment(sheetId: string): Promise<AxiosResponse<string>> {
  return await axios({
    method: 'DELETE',
    url: `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/document`,
  });
}

export async function createExam(sessionId: string, name: string, code: string): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/Exams/sessions/${sessionId}/exams`, {
    name: name,
    code: code,
  });

  return response;
}

export async function createExamSheet(
  id: string,
  code: string,
  name: string,
  gradeRulesImageFilePath: File | undefined,
): Promise<AxiosResponse<ExamSheet>> {
  const requestBody = new FormData();
  requestBody.append('ExamId', id);
  requestBody.append('Code', code);
  requestBody.append('Name', name);

  if (gradeRulesImageFilePath) {
    requestBody.append('FormFile', gradeRulesImageFilePath);
  }

  const response = await axios({
    method: 'POST',
    url: `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
  });

  return response;
}

export async function createExamTaskGroup(
  examSheetId: string,
  name: string,
  dateFrom: string,
  dateTo: string,
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTaskGroup`, {
    examSheetId: examSheetId,
    name: name,
    dateFrom: dateFrom,
    dateTo: dateTo,
  });

  return response;
}

export async function createExamTask(
  examTaskGroupId: string,
  examTaskNumber: string,
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask`, {
    examTaskGroupId: examTaskGroupId,
    examTaskNumber: examTaskNumber,
  });

  return response;
}

export async function AssignExamTaskToExamTaskGroup(
  examTaskGroupId: string,
  ExamTaskId: string,
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.put(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamTask/TaskGroup`, {
    examTaskGroupId: examTaskGroupId,
    ExamTaskId: ExamTaskId,
  });

  return response;
}

export async function createExamScoringSchema(
  examGradeRuleId: string,
  label: string,
  criterionName: string,
  criterionNumber: string,
  examScoreValues: number[],
): Promise<AxiosResponse<IIdResult>> {
  const response = await axios.post(EnvWindow.REACT_APP_EXAMS__EXAM_SCORING_SCHEMA_API_URL, {
    examGradeRuleId: examGradeRuleId,
    label: label,
    criterionName: criterionName,
    criterionNumber: criterionNumber,
    examScoreValues: examScoreValues,
  });

  return response;
}

export async function getCardLimits(
  examSheetId: string | number,
  isExaminerView?: boolean,
): Promise<{ cardLimits: CardLimits[] }> {
  if (isExaminerView) {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/CardLimits`);

    return { cardLimits: [response.data] };
  } else {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/CardLimit/${examSheetId}`);

    return response.data;
  }
}

export async function calculateCardLimits(examSheetId: string): Promise<{ data: CardLimits[] }> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/CardLimit/${examSheetId}/calculate`);

  return response;
}

export const saveCardLimits = async (
  body: CardLimitsData,
  isExaminerView?: boolean,
): Promise<AxiosResponse<UpdateCardLimitsResponse>> => {
  if (isExaminerView) {
    return await axios.post(`${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/CardLimits`, body.cardLimits[0]);
  } else return await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/CardLimit/create/bulk/`, body);
};

export const getSheetGradeRuleName = async (assessmentSessionId: string): Promise<AxiosResponse<string | null>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/examSheet/gradeRuleDocumentName`,
  );

  return response;
};

export async function getExaminersTreemapData(
  examSheetId: string,
  examinerName: string,
  examinerSurname: string,
  examinerNumber: string,
  groupTaskName: string,
  functionFilter: string,
  examCommitteeFilter: string,
  examinerMode: boolean,
  signal?: AbortSignal,
): Promise<ITaskGroup[]> {
  let params = '';
  params += addParam('examinerName', examinerName ? examinerName : '');
  params += addParam('examinerSurname', examinerSurname ? examinerSurname : '');
  params += addParam('examinerNumber', examinerNumber ? examinerNumber : '');
  params += addParam('groupTaskName', groupTaskName ? groupTaskName : '');
  params += addParam('functionFilter', functionFilter ? functionFilter : '');
  params += splitParams('examCommitteeFilter', examCommitteeFilter ? examCommitteeFilter : '');

  if (params !== '' && params[0] === '&') params = params.substring(1);

  if (!examinerMode) {
    const response = await axios.get(
      `${EnvWindow.REACT_APP_EXAMS_API_URL}/examSheet/${examSheetId}/examinerStructure?${params}`,
      { signal: signal ?? undefined },
    );

    return response.data;
  } else {
    const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/examSheet/examinerStructure?${params}`, {
      signal: signal ?? undefined,
    });

    return response.data;
  }
}

export async function uploadExamSheetFile(
  examSheetId: string,
  file: File,
  signal?: AbortSignal,
): Promise<AxiosResponse> {
  const requestBody = new FormData();

  if (file) {
    requestBody.append('File', file);
  }

  const response = await axios({
    method: 'PUT',
    url: `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/sample/page`,
    headers: { 'Content-Type': 'multipart/form-data' },
    data: requestBody,
    signal,
  });

  return response;
}

export const getUploadedExamSheetFiles = async (examSheetId: string): Promise<AxiosResponse<ExamSheetFile[]>> => {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/sample`);

  return response;
};

export function getCanExamSheetBeConfigured(sheetId: string): Promise<AxiosResponse<CanExamSheetBeConfiguredResults>> {
  const response = axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/CanBeConfigured`);

  return response;
}

export function setExamSheetAsConfigured(sheetId: string): Promise<AxiosResponse<CanExamSheetBeConfiguredResults>> {
  const response = axios.patch(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/Configure`);

  return response;
}

export async function deleteSheetPage(sheetId: string, sheetPageList: number[]): Promise<AxiosResponse> {
  const pageNumberList = () => {
    return '?' + 'pageNumberList=' + sheetPageList.map(encodeURIComponent).join('&pageNumberList=');
  };

  return await axios.delete(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${sheetId}/sample/pages${pageNumberList()}`,
  );
}

export const getExamSheetThumbnail = async (
  examSheetId: string,
  pageNumber: number,
): Promise<AxiosResponse<ArrayBuffer>> => {
  const response = await axios.get(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheetId}/sample/page/${pageNumber}`,
    {
      responseType: 'arraybuffer',
    },
  );

  return response;
};

export function getCanSessionBeOpen(assessmentSessionId: string): Promise<AxiosResponse<CanSessionBeSetToOpen>> {
  const response = axios.get(
    `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/canBeOpen`,
  );

  return response;
}

export function getCanSessionBeClosed(
  assessmentSessionId: string | null,
): Promise<AxiosResponse<CanSessionBeSetToClosed>> {
  const response = axios.get(
    assessmentSessionId
      ? `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/${assessmentSessionId}/canBeClosed`
      : `${EnvWindow.REACT_APP_EXAMS__ASSESSMENT_SESSION_API_URL}/canBeClosed`,
  );

  return response;
}

export function exportResultsToSioepkz(assessmentSessionId: string | null): Promise<AxiosResponse> {
  const response = axios.post(`${EnvWindow.REACT_APP_RESULTS_API_URL}/ExamSheet/${assessmentSessionId}/export`);

  return response;
}

export async function exportExamResults(
  examSheetId: string,
  schoolId?: string,
  examineeIdentity?: string,
  fileType?: FileType,
): Promise<Blob> {
  let params = '';
  params += addParam('schoolCode', schoolId ? schoolId : '');
  params += addParam('examineeIdentity', examineeIdentity ? examineeIdentity : '');
  params += addParam('resultAsJson', fileType === FileType.json ? 'true' : 'false');

  const response = await axios.get(`${EnvWindow.REACT_APP_RESULTS_API_URL}/ExamSheet/${examSheetId}/file?${params}`);

  return response.data;
}

export async function exportExamDocumentation(
  annotations: boolean,
  scoreDetails: boolean,
  examinerDetails: boolean,
  sheetCode: string,
  examSheetId: string,
  examineeIdentity?: string | null,
  schoolCode?: string | null,
  isSioepkz?: boolean,
): Promise<BackendErrorCode> {
  const response = await axios.put(`${EnvWindow.REACT_APP_RESULTS_API_URL}/Results/Report`, {
    examineeIdentity,
    schoolCode,
    annotations,
    scoreDetails,
    examinerDetails,
    sheetCode,
    examSheetId,
    isSioepkz,
  });

  return response.data;
}

export async function startCopyingProcess(
  examSheet: string,
  copyInfo: CopyExamSheetDto,
  signal?: AbortSignal,
): Promise<CopyExamSheetResponse> {
  const response = await axios.post(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSheet/${examSheet}/copy`, copyInfo, {
    signal,
  });

  return response.data;
}

export async function getSessionsFromSystemOne(signal: AbortSignal): Promise<ExamSessionSimpleDto[]> {
  const response = await axios.get(`${EnvWindow.REACT_APP_EXAMS_API_URL}/ExamSession/list`, { signal });

  return response.data;
}

export async function getLatestCopyExamSheetOperation(
  examSheetId: string,
  signal: AbortSignal,
): Promise<CopyExamSheetOperation | null> {
  const response = await axios.get<CopyExamSheetOperation | null>(
    `${EnvWindow.REACT_APP_EXAMS_API_URL}/copy-operations/latest`,
    { params: { examSheetId }, signal },
  );

  return response.data;
}
